import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
} from '@material-ui/core';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import GestureIcon from '@material-ui/icons/Gesture';
import BrushIcon from '@material-ui/icons/Brush';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '../../../../../components/Button';
import {
  SOS_AREA_REQUIRED_BRAND,
  getAvailableBrandOptions,
  getOrderedPaintItems,
} from './brandColorOptions';

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  drawingTypeContainer: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  lengthValue: {
    fontWeight: 'normal',
  },
  iconWrapper: {
    borderRadius: '8px',
    padding: '14px 14px 15px 14px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'transform 0.1s ease',
    '&:active': {
      transform: 'scale(0.9)',
    },
  },
}));

const PolygonAreasTable = ({
  polygonAreas,
  polygonColor,
  setPolygonColor,
  colorOptions,
  setColorOptions,
  sosPercentages,
  onClearPolygonByColor,
  onClearAllVectors,
  onCopyVectorsByColor,
  availableCopyColors = {},
  teleauditStatus,
  brandOptions = [],
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  // Needed because several colours below live in inline `style` props.
  const theme = useTheme();

  if (!polygonAreas) return null;

  // El área es por color: reasignar la marca de un color no la mueve ni la pierde.
  const areaMap = polygonAreas.areasByColor || {};

  function PaintItem({
    colorName,
    colorValue,
    setPolygonColor,
    onClearPolygonByColor,
    onCopyVectorsByColor,
    option,
    setOption,
    teleauditStatus,
  }) {
    const value = Math.round(areaMap[colorName] || 0);
    const availableCopyColor = availableCopyColors[colorName];

    return (
      <Grid container item xs={12} direction="column" style={{ minWidth: 500 }}>
        <Box
          display="grid"
          gridTemplateColumns="minmax(190px, 1fr) minmax(70px, 1fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(100px, 1fr)"
          alignItems="center"
          mb={2}
          style={{ width: '100%', minWidth: 500, margin: '0 auto' }}
        >
          {/* Select */}
          <FormControl
            variant="outlined"
            size="small"
            style={{ width: '100%', minWidth: 0 }}
          >
            <InputLabel>Opciones</InputLabel>
            <Select
              value={option}
              disabled={teleauditStatus === 'done' || option === SOS_AREA_REQUIRED_BRAND.brandId}
              onChange={(e) => setOption(Number(e.target.value))}
              label="Opciones"
            >
              {getAvailableBrandOptions(
                brandOptions,
                colorOptions,
                colorName,
                option,
              ).map((brandOption) => (
                <MenuItem key={brandOption.value} value={brandOption.value}>
                  {brandOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Button */}
          <BottomNavigation
            showLabels
            style={{ width: '100%', minWidth: 0 }}
          >
            <BottomNavigationAction
              disableRipple
              disabled={(teleauditStatus === 'done' || option === 0)}
              onClick={() => setPolygonColor(colorName)}
              style={{ width: '100%', minWidth: 0 }}
              icon={
                <Box
                  className={styles.iconWrapper}
                  style={{
                    backgroundColor: option === 0 ? '#dfdfdfff' : '#fff',
                    border:
                      polygonColor !== colorName
                        ? `1px solid ${theme.palette.custom.hairline}`
                        : `2px solid ${colorValue}`,
                  }}
                >
                  <GestureIcon style={{ color: colorValue }} />
                </Box>
              }
            />
          </BottomNavigation>

          {/* Single Progress Bar for selected option */}
          <Box style={{ width: '100%', minWidth: 0, textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="div"
              style={{ color: colorValue, fontWeight: 'bold' }}
            >
              {value}
            </Typography>
          </Box>
          <Box style={{ width: '100%', minWidth: 0 }}>
            <Typography
              variant="h4"
              component="div"
              color="textSecondary"
              style={{ textAlign: 'center' }}
            >
              {sosPercentages[colorName]
                ? `${sosPercentages[colorName].toFixed(0)}%`
                : '0%'}
            </Typography>
          </Box>
          <Box
            style={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
            }}
          >
            <IconButton
              aria-label={t('teleaudit.copyStrokes')}
              disabled={teleauditStatus === 'done' || value === 0 || !availableCopyColor}
              onClick={() => onCopyVectorsByColor(colorName)}
              title={t('teleaudit.copyStrokesTooltip')}
              style={{
                width: 50,
                minWidth: 50,
                height: 56,
                padding: 8,
                color: teleauditStatus === 'done' || !availableCopyColor
                  ? '#9e9e9e'
                  : '#2C9DC9',
                flex: '1 1 50px',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 20 }}>⧉</span>
            </IconButton>
            <IconButton
              aria-label={t('teleaudit.deleteLines')}
              disabled={teleauditStatus === 'done'}
              onClick={() => onClearPolygonByColor(colorName)}
              title={t('teleaudit.deleteLines')}
              style={{
                width: 50,
                minWidth: 50,
                height: 56,
                padding: 8,
                color: teleauditStatus === 'done' ? '#9e9e9e' : '#d32f2f',
                flex: '1 1 50px',
                flexShrink: 0,
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          {/* <TextField
            name="target"
            type="text"
            size="medium"
            placeholder="Target"
            variant="outlined"
            style={{ width: 150 }}
          /> */}
        </Box>
      </Grid>
    );
  }

  const orderedPaintItems = getOrderedPaintItems(brandOptions, colorOptions);

  return (
    <Box className={styles.buttonContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mt={4} mb={4} display="flex" direction="row" alignItems="center">
          <BrushIcon color="primary" />
          <Typography variant="h2">Dibujar trazos</Typography>
        </Box>
        <Button
          id="clear-all-vectors-btn"
          text={t('teleaudit.deleteStrokes')}
          variant="contained"
          height="36px"
          width="170px"
          backgroundColor={teleauditStatus === 'done' ? '#D3D3D3' : '#2C9DC9'}
          color="white"
          borderRadius="10px"
          disabled={teleauditStatus === 'done'}
          onClick={onClearAllVectors}
        />
      </Box>
      <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
        <Grid item xs={12}>
          <Box
            display="grid"
            gridTemplateColumns="minmax(190px, 1fr) minmax(70px, 1fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(100px, 1fr)"
            alignItems="center"
            style={{ width: '100%', minWidth: 500, margin: '0 auto' }}
          >
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              Marca
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              Color
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                textAlign: 'center',
                whiteSpace: 'normal',
                lineHeight: 1.2,
              }}
            >
              Longitud (px²)
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              %
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {t('teleaudit.actions')}
            </Typography>
            {/* <Typography
                      variant="h4"
                      style={{ width: 150, minWidth: 150, flex: '1 1 150px', textAlign: 'center' }}
                    >
                      Target
                    </Typography> */}
          </Box>
        </Grid>

        {orderedPaintItems.map((item) => (
          <PaintItem
            key={item.key}
            colorName={item.key}
            colorValue={item.color}
            setPolygonColor={setPolygonColor}
            onClearPolygonByColor={onClearPolygonByColor}
            onCopyVectorsByColor={onCopyVectorsByColor}
            option={colorOptions[item.key]}
            teleauditStatus={teleauditStatus}
            setOption={(newValue) => setColorOptions((prev) => ({
              ...prev,
              [item.key]: newValue,
            }))
            }
          />
        ))}
      </Grid>

    </Box>
  );
};

export default PolygonAreasTable;
